import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search, Syringe, ClipboardList, Filter } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useEscapeKey } from '../hooks/useEscapeKey';
import {
  getImmunizationCatalog,
  createCatalogItem,
  updateCatalogItem,
  deleteCatalogItem,
  hardDeleteCatalogItem
} from '../utils/immunizationCatalogApi';
import { setInventoryItemStockTracked } from '../utils/inventoryApi';
import { ImmunizationCatalogItem } from '../types/immunization-catalog';
import ImmunizationCatalogItemModal from '../components/immunizations/ImmunizationCatalogItemModal';
import PracticeTemplatesManagement from '../components/immunizations/PracticeTemplatesManagement';
import ConfirmDialog from '../components/common/ConfirmDialog';

type TabType = 'catalog' | 'templates';

const ImmunizationCatalogManagement: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Initialize tab from URL query parameter, default to 'catalog'
  const initialTab = searchParams.get('tab') === 'templates' ? 'templates' : 'catalog';
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  
  // Update URL when tab changes
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    if (tab === 'templates') {
      next.set('tab', 'templates');
    } else {
      next.delete('tab');
    }
    setSearchParams(next);
  };
  const [items, setItems] = useState<ImmunizationCatalogItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<ImmunizationCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedItem, setSelectedItem] = useState<ImmunizationCatalogItem | null>(null);
  const [showToggleConfirm, setShowToggleConfirm] = useState(false);
  const [itemToToggle, setItemToToggle] = useState<ImmunizationCatalogItem | null>(null);
  const [showInactive, setShowInactive] = useState(() => {
    return localStorage.getItem('catalog_showInactive') === 'true';
  });
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const filterMenuRef = useRef<HTMLDivElement>(null);

  useEscapeKey(() => setShowFilterMenu(false), showFilterMenu);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!showFilterMenu) return;
      const target = event.target as Node;
      if (filterMenuRef.current && !filterMenuRef.current.contains(target)) {
        setShowFilterMenu(false);
      }
    };
    // Use capture so this still runs even if inner elements call stopPropagation()
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [showFilterMenu]);

  // Persist filter preference
  const handleShowInactiveChange = (value: boolean) => {
    setShowInactive(value);
    localStorage.setItem('catalog_showInactive', String(value));
  };

  useEffect(() => {
    if (user?.practice_id) {
      fetchItems();
    }
  }, [user?.practice_id]);

  const clearModalParams = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('create');
    next.delete('edit');
    setSearchParams(next, { replace: true });
  };

  const closeModal = () => {
    setShowModal(false);
    clearModalParams();
  };

  // URL-driven modal open (RESTful / back-button friendly)
  useEffect(() => {
    const create = searchParams.get('create');
    const editId = searchParams.get('edit');
    if (create === '1') {
      handleCreate();
      return;
    }
    if (editId) {
      navigate(`/settings/immunization-catalog/${editId}?edit=1`, { replace: true });
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, items]);

  useEffect(() => {
    filterItems();
  }, [items, searchTerm, showInactive]);

  const fetchItems = async () => {
    if (!user?.practice_id) return;

    setLoading(true);
    try {
      const data = await getImmunizationCatalog(user.practice_id, undefined, false); // Get all items including inactive
      setItems(data);
    } catch (err: any) {
      console.error('Failed to fetch catalog:', err);
      alert('Failed to load catalog items: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const filterItems = () => {
    let filtered = items;

    // Filter by active status (hide inactive unless toggle is on)
    if (!showInactive) {
      filtered = filtered.filter(item => item.is_active);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(term) ||
        (item.default_manufacturer && item.default_manufacturer.toLowerCase().includes(term)) ||
        (item.notes && item.notes.toLowerCase().includes(term))
      );
    }

    setFilteredItems(filtered);
  };

  const handleCreate = () => {
    setSelectedItem(null);
    setModalMode('create');
    setShowModal(true);
    const next = new URLSearchParams(searchParams);
    next.set('create', '1');
    next.delete('edit');
    setSearchParams(next);
  };

  const handleEdit = (item: ImmunizationCatalogItem) => {
    if (item.is_standard) {
      // For standard items, we'll create a practice-specific override
      // Pre-fill the modal with the standard item's data
      setSelectedItem(item);
      setModalMode('create'); // Use create mode to make a new practice item
      setShowModal(true);
    } else {
      // For practice items, normal edit
      setSelectedItem(item);
      setModalMode('edit');
      setShowModal(true);
    }
    const next = new URLSearchParams(searchParams);
    next.set('edit', item.id);
    next.delete('create');
    setSearchParams(next);
  };

  const handleSave = async (data: any) => {
    if (!user?.practice_id) return;

    if (modalMode === 'create') {
      // Determine based_on_standard_id:
      // 1. If clicking on a standard item, use its ID
      // 2. If clicking on a practice item that has based_on_standard_id, chain through to original
      // 3. Otherwise, no based_on_standard_id (brand new item)
      let basedOnStandardId: string | null = null;
      if (selectedItem?.is_standard) {
        basedOnStandardId = selectedItem.id;
      } else if (selectedItem?.based_on_standard_id) {
        basedOnStandardId = selectedItem.based_on_standard_id;
      }
      
      const createData = basedOnStandardId
        ? { ...data, based_on_standard_id: basedOnStandardId }
        : data;
      await createCatalogItem(user.practice_id, createData);
    } else if (selectedItem) {
      await updateCatalogItem(user.practice_id, selectedItem.id, data);
    }

    await fetchItems();
  };

  const handleHardDelete = async (itemId: string) => {
    if (!user?.practice_id) return;
    
    await hardDeleteCatalogItem(user.practice_id, itemId);
    await fetchItems();
  };

  const handleToggleActive = (item: ImmunizationCatalogItem) => {
    setItemToToggle(item);
    setShowToggleConfirm(true);
  };

  const handleToggleConsumable = async (item: ImmunizationCatalogItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user?.practice_id) return;
    
    try {
      const newValue = !item.is_stock_tracked;
      await setInventoryItemStockTracked(user.practice_id, 'immunization', item.id, newValue);
      setItems(prev => prev.map(i => 
        i.id === item.id ? { ...i, is_stock_tracked: newValue } : i
      ));
    } catch (err: any) {
      alert('Failed to update consumable status: ' + err.message);
    }
  };

  const confirmToggleActive = async () => {
    if (!itemToToggle || !user?.practice_id) return;

    try {
      // Toggle is_active status
      await updateCatalogItem(user.practice_id, itemToToggle.id, { 
        is_active: !itemToToggle.is_active 
      });
      await fetchItems();
    } catch (err: any) {
      alert('Failed to update item: ' + err.message);
    } finally {
      setShowToggleConfirm(false);
      setItemToToggle(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <span className="ml-4 text-lg text-gray-600">Loading catalog...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Syringe className="w-6 h-6 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Immunization Settings</h1>
          </div>
          <p className="text-gray-600 ml-14">
            Manage your vaccine catalog and create custom vaccination protocols. Pricing is managed in the <a href="/settings/catalog" className="text-blue-600 hover:underline">Billing Catalog</a>.
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <nav className="flex space-x-4 border-b border-gray-200">
            <button
              onClick={() => handleTabChange('catalog')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'catalog'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Syringe className="w-4 h-4" />
                Vaccine Catalog
              </div>
            </button>
            <button
              onClick={() => handleTabChange('templates')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'templates'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <ClipboardList className="w-4 h-4" />
                Vaccination Protocols
              </div>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'templates' ? (
          <PracticeTemplatesManagement />
        ) : (
          <>
            {/* Search and Actions */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-6">
              <div className="flex flex-col md:flex-row gap-4">
                {/* Search */}
                <div className="relative flex-1 min-w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search immunizations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Add Custom Button */}
                <button
                  onClick={handleCreate}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Add Custom Immunization
                </button>
              </div>
            </div>

            {/* Filter Dropdown - Between boxes */}
            <div className="relative mb-4" ref={filterMenuRef}>
              <button
                onClick={() => setShowFilterMenu(!showFilterMenu)}
                className="p-2 rounded-lg border bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600 transition-colors"
                title="Filter options"
              >
                <Filter className="w-5 h-5" />
              </button>
              
              {showFilterMenu && (
                <div className="absolute top-full left-0 mt-2 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 min-w-48">
                  <button
                    onClick={() => {
                      handleShowInactiveChange(!showInactive);
                      setShowFilterMenu(false);
                    }}
                    className="w-full px-4 py-3 text-left text-sm text-gray-200 hover:bg-gray-700 rounded-lg"
                  >
                    {showInactive ? 'Hide Inactive' : 'Show Inactive'}
                  </button>
                </div>
              )}
            </div>

        {/* Items Table */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Site</th>
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Duration</th>
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Manufacturer</th>
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-center">Consumable</th>
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Active</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      <Syringe className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-lg font-medium text-gray-900">No immunizations found</p>
                      <p className="text-sm text-gray-500">Add a custom immunization to get started.</p>
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((item) => (
                    <tr
                      key={item.id}
                      onClick={() => navigate(`/settings/immunization-catalog/${item.id}`)}
                      className={`hover:bg-gray-50 transition-colors cursor-pointer ${!item.is_active ? 'opacity-60' : ''}`}
                    >
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{item.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{item.default_site || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {item.typical_duration_days ? `${item.typical_duration_days} days` : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{item.default_manufacturer || '-'}</td>
                      <td className="px-6 py-4 text-sm text-center">
                        <button
                          onClick={(e) => handleToggleConsumable(item, e)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                            item.is_stock_tracked 
                              ? 'bg-green-500 focus:ring-green-500' 
                              : 'bg-gray-300 focus:ring-gray-400'
                          }`}
                          title={item.is_stock_tracked ? 'Inventory tracked - click to disable' : 'Not inventory tracked - click to enable'}
                          role="switch"
                          aria-checked={item.is_stock_tracked || false}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform duration-200 ease-in-out ${
                              item.is_stock_tracked ? 'translate-x-4' : 'translate-x-0.5'
                            }`}
                          />
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleActive(item);
                          }}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                            item.is_active 
                              ? 'bg-green-500 focus:ring-green-500' 
                              : 'bg-gray-300 focus:ring-gray-400'
                          }`}
                          title={item.is_active ? 'Click to deactivate' : 'Click to activate'}
                          role="switch"
                          aria-checked={item.is_active}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform duration-200 ease-in-out ${
                              item.is_active ? 'translate-x-4' : 'translate-x-0.5'
                            }`}
                          />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
            </div>
          </>
        )}
      </div>

      {/* Modal */}
      <ImmunizationCatalogItemModal
        isOpen={showModal}
        onClose={() => {
          closeModal();
          setSelectedItem(null);
        }}
        onSave={handleSave}
        onHardDelete={handleHardDelete}
        item={selectedItem}
        mode={modalMode}
      />

      {/* Toggle Confirm Dialog */}
      {showToggleConfirm && itemToToggle && (
        <ConfirmDialog
          isOpen={showToggleConfirm}
          title={itemToToggle.is_active ? 'Deactivate Item' : 'Activate Item'}
          message={
            itemToToggle.is_active
              ? `Are you sure you want to deactivate "${itemToToggle.name}"? It will no longer appear in the immunization dropdown.`
              : `Are you sure you want to activate "${itemToToggle.name}"?`
          }
          confirmText={itemToToggle.is_active ? 'Deactivate' : 'Activate'}
          onConfirm={confirmToggleActive}
          onClose={() => {
            setShowToggleConfirm(false);
            setItemToToggle(null);
          }}
        />
      )}
    </div>
  );
};

export default ImmunizationCatalogManagement;



